import { ampli } from '@/ampli';
import { Title } from '@radix-ui/react-dialog';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { useRouter } from 'next/router';
import { ReactNode, createContext, useEffect, useState } from 'react';
import { useMedia } from 'react-use';

import { updateCheckoutStepSelector, useTradeStore } from '../../../store/trade';
import { TradeStore } from '../../../store/trade/index';
import { breakpoints } from '../../../styles/util';
import Modal from '../../../ui/Modal';
import Panel from '../../../ui/Panel';
import { TradeModuleStep, useUpdateRouteTradeModuleStep } from '../utils';
import TransactionModuleMobileCTA from './TransactionModuleMobileCTA';

interface Props {
  isTokenBlocked: boolean;
  children: ReactNode;
}
const checkoutSelector = ({ resetCheckout }: TradeStore) => ({
  resetCheckout,
});

const getTradeModuleStepFromUrlString = (url: string): TradeModuleStep | undefined => {
  let parts = url.split('/');
  if (parts.length < 5) return undefined;
  parts = parts[4].split('?');
  return parts[0] as TradeModuleStep;
};
export const TransactionModuleContainerContext = createContext<{ modal: boolean }>({
  modal: false,
});

const TransactionModuleContainer = ({ isTokenBlocked, children }: Props) => {
  const router = useRouter();
  const isMobile = useMedia(breakpoints.mobile, false);
  const [transactionModuleOpen, setTransactionModuleOpen] = useState(false);
  const { resetCheckout } = useTradeStore(checkoutSelector);
  const updateCheckoutStep = useTradeStore(updateCheckoutStepSelector);
  const updateRouteTradeModuleStep = useUpdateRouteTradeModuleStep();
  const [hasSetInitialTradeModuleStep, setHasSetInitialTradeModuleStep] = useState(false);

  // Load the correct trade module step on page load
  useEffect(() => {
    // If the router is not ready yet, or we've already set the initial trade module step, stop here
    if (!isMobile || !router.isReady || hasSetInitialTradeModuleStep) return;
    // If the user has a query param for the trade module step
    setHasSetInitialTradeModuleStep(true);
    if (!router.query.contractAddresses?.length || !router.query.contractAddresses[1]) return;
    // Force the 'select' step, since it's the starting one
    updateCheckoutStep('select');
    updateRouteTradeModuleStep('select');
    setTransactionModuleOpen(true);
  }, [
    isMobile,
    hasSetInitialTradeModuleStep,
    router,
    updateCheckoutStep,
    updateRouteTradeModuleStep,
  ]);

  // Update the trade module step on page navigation
  useEffect(() => {
    if (!isMobile) return;

    // Manually manage the browser's back and forward events
    router.beforePopState(({ as, url }) => {
      // Skip history handling as user is navigating to another page
      if (!url.includes('/tokens')) {
        return true;
      }

      // If there is no next trade module step, close the trading modal
      const nextTradeModuleStep = getTradeModuleStepFromUrlString(as);
      ampli.transactionStepBack({ ui_location: 'transaction_module_mobile' });

      if (nextTradeModuleStep === 'select') {
        updateCheckoutStep(nextTradeModuleStep);
        updateRouteTradeModuleStep(nextTradeModuleStep);
        setTransactionModuleOpen(true);
      } else {
        resetCheckout();
        updateRouteTradeModuleStep(undefined);
        setTransactionModuleOpen(false);
      }

      return false;
    });

    // Cleanup, restore the default behavior
    return () => router.beforePopState(() => true);
  }, [isMobile, resetCheckout, router, updateCheckoutStep, updateRouteTradeModuleStep]);

  function handleOpen() {
    setTransactionModuleOpen(true);
    updateRouteTradeModuleStep('select');
  }

  function handleClose() {
    ampli.transactionStepBack({ ui_location: 'transaction_module_close' });
    resetCheckout();
    setTransactionModuleOpen(false);
    updateRouteTradeModuleStep(undefined);
  }

  let content = (
    <Panel className="relative z-[1] hidden tablet:block overflow-hidden shadow-30">
      {children}
    </Panel>
  );

  if (isMobile) {
    content = (
      <>
        <Modal
          onClose={handleClose}
          isOpen={transactionModuleOpen}
          onOpenChange={setTransactionModuleOpen}
        >
          <div className="flex justify-end px-2 pb-0 pt-2 absolute z-[1] right-0">
            <VisuallyHidden>
              <Title>Trade</Title>
            </VisuallyHidden>
            {/* Close button no longer needed since we only have select state */}
          </div>
          {children}
        </Modal>
        <div className="fixed bottom-0 left-0 flex justify-center w-full px-6 pb-4">
          <TransactionModuleMobileCTA isTokenBlocked={isTokenBlocked} onClick={handleOpen} />
        </div>
      </>
    );
  }

  return (
    <TransactionModuleContainerContext.Provider value={{ modal: isMobile }}>
      {content}
    </TransactionModuleContainerContext.Provider>
  );
};

export default TransactionModuleContainer;
